import { supabase } from './cloud/supabaseClient';

export type DashboardRange = { from: string; to: string };
export type DashboardKpis = {
  sales_centavos: number;
  order_count: number;
  kg_processed: number;
  cash_centavos: number;
  gcash_centavos: number;
  maya_centavos: number;
  pending_orders: number;
  unclaimed_orders: number;
};
export type DailySalesRow = { day: string; order_count: number; sales_centavos: number };
export type PaymentRow = {
  method: 'CASH' | 'GCASH' | 'MAYA';
  payment_count: number;
  amount_centavos: number;
};
export type ServiceRow = {
  service_name: string;
  order_count: number;
  kg_volume: number;
  sales_centavos: number;
};
export type KgRow = { day: string; kg_volume: number };
export type VoidedRow = {
  order_number: string;
  total_centavos: number;
  voided_at: string;
  cashier_name: string;
  reason: string;
};
export type CashierRow = {
  cashier_name: string;
  order_count: number;
  sales_centavos: number;
  claimed_count: number;
};
export type PendingRow = {
  order_number: string;
  order_status: string;
  payment_status: string;
  total_centavos: number;
  created_at: string;
};
export type SyncHealthRow = {
  device_id: string;
  device_name: string;
  device_type: string;
  last_sync_at: string | null;
  pending_transactions: number;
  sync_status: string;
  active: boolean;
};
export type AuditLogRow = {
  audit_id: string;
  created_at: string;
  actor_name: string;
  actor_role: 'owner' | 'manager' | 'cashier' | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  metadata: Record<string, unknown>;
};

async function rpc<T>(name: string, args: Record<string, unknown>): Promise<T[]> {
  const { data, error } = await supabase.rpc(name as never, args as never);
  if (error) throw error;
  return (data ?? []) as T[];
}

export async function loadDashboard(range: DashboardRange) {
  const [
    kpis,
    dailySales,
    payments,
    services,
    kgVolume,
    voidedOrders,
    cashierActivity,
    pendingOrders,
    syncHealth,
    auditLogs,
  ] = await Promise.all([
    rpc<DashboardKpis>('owner_dashboard_kpis', { p_from: range.from, p_to: range.to }),
    rpc<DailySalesRow>('owner_daily_sales', { p_from: range.from, p_to: range.to }),
    rpc<PaymentRow>('owner_payment_breakdown', { p_from: range.from, p_to: range.to }),
    rpc<ServiceRow>('owner_services_report', { p_from: range.from, p_to: range.to }),
    rpc<KgRow>('owner_kg_volume_report', { p_from: range.from, p_to: range.to }),
    rpc<VoidedRow>('owner_voided_orders_report', { p_from: range.from, p_to: range.to }),
    rpc<CashierRow>('owner_cashier_activity_report', { p_from: range.from, p_to: range.to }),
    rpc<PendingRow>('owner_pending_orders_report', { p_from: range.from, p_to: range.to }),
    rpc<SyncHealthRow>('owner_sync_health', {}),
    rpc<AuditLogRow>('owner_audit_logs', { p_from: range.from, p_to: range.to }),
  ]);
  return {
    kpis: kpis[0] ?? {
      sales_centavos: 0,
      order_count: 0,
      kg_processed: 0,
      cash_centavos: 0,
      gcash_centavos: 0,
      maya_centavos: 0,
      pending_orders: 0,
      unclaimed_orders: 0,
    },
    dailySales,
    payments,
    services,
    kgVolume,
    voidedOrders,
    cashierActivity,
    pendingOrders,
    syncHealth,
    auditLogs,
  };
}
